import React, {ReactElement, createRef, RefObject, ReactNode} from 'react';
import {FilterMatchMode} from 'primereact/api';
import {Column, ColumnFilterElementTemplateOptions} from 'primereact/column';
import {Calendar} from 'primereact/calendar';
import MComponent from '../../Toolkit/Components/Component';
import WithRouterProps from '../../Toolkit/WithRouterProps';
import MPage, {MPageProps} from '../../Toolkit/Components/Shell/Page';
import MTable, {MTableProps} from '../../Toolkit/Components/Controls/Table';
import MToolbar from '../../Toolkit/Components/Controls/Toolbar';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import MButton from '../../Toolkit/Components/Controls/Button';
import {Request, RequestMultiReply} from '../../Api/ControllerTypes/RequestTypes';
import WithRouter from '../../Toolkit/WithRouter';
import {RequestClient} from '../../Api/RequestClients';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import NSwagAdapter from '../../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../../Toolkit/Api/NSwagClientConfiguration';
import SafeDigAccessTokenProvider from '../../Api/TrafficManagementAccessTokenProvider';
import MMultiDropDown from '../../Toolkit/Components/Controls/MultiDropDown';
import {MDropDownItem} from '../../Toolkit/Components/Controls/DropDownOption';
import {CreateLockRequest, LockClient} from '../../Api/LockClient';
import {LockReply} from '../../Api/ControllerTypes/LockTypes';
import {MSuccessIcon} from '../../Toolkit/Icons';
import MessageDialog from '../../Dialogs/MessageDialog';
import EnumContext from '../../Context/enum-context';

type RequestRow = {
    data: Request;
}

type Props = WithMsalProps & WithRouterProps & {};

/** */
type State = {
    page: MPageProps;
    table: MTableProps<RequestRow>;
    selectedRow?: RequestRow;
}

class ListBookingsPage extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>;

    private readonly m_requestClient: NSwagAdapter<RequestClient>;
    private readonly m_lockClient: NSwagAdapter<LockClient>;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_messageDialogRef: RefObject<MessageDialog>;
    private m_interval: any = 0;

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_requestClient = new NSwagAdapter<RequestClient>(new RequestClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_lockClient = new NSwagAdapter<LockClient>(new LockClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_pageRef = createRef<MPage>();
        this.m_messageDialogRef = createRef<MessageDialog>();

        this.initializeState();

    }

    private bindMethods(): void {

        this.isOpenButtonDisabled = this.isOpenButtonDisabled.bind(this);
        this.requests_Click = this.requests_Click.bind(this);
        this.crews_Click = this.crews_Click.bind(this);
        this.calendarView_Click = this.calendarView_Click.bind(this);
        this.dayView_Click = this.dayView_Click.bind(this);
        this.invoices_Click = this.invoices_Click.bind(this);
        this.drawings_Click = this.drawings_Click.bind(this);
        this.commercials_Click = this.commercials_Click.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.getSlaFilterTemplate = this.getSlaFilterTemplate.bind(this);
        this.slaTypeTemplate = this.slaTypeTemplate.bind(this);
        this.getProjectFilterTemplate = this.getProjectFilterTemplate.bind(this);
        this.getWorkStreamFilterTemplate = this.getWorkStreamFilterTemplate.bind(this);
        this.getPriorityFilterTemplate = this.getPriorityFilterTemplate.bind(this);
        this.getQuoteStatusFilterTemplate = this.getQuoteStatusFilterTemplate.bind(this);
        this.getJobStatusFilterTemplate = this.getJobStatusFilterTemplate.bind(this);
        this.getPermitStatusFilterTemplate = this.getPermitStatusFilterTemplate.bind(this);
        this.quoteStatusTemplate = this.quoteStatusTemplate.bind(this);
        this.jobStatusTemplate = this.jobStatusTemplate.bind(this);
        this.permitStatusTemplate = this.permitStatusTemplate.bind(this);
    }

    /** */
    private initializeState(): void {

        //  Page
        const page: MPageProps = {
            locked: false
        };

        //  Table
        const table: MTableProps<RequestRow> = {
            rowData: [],
            filters: {
                'data.estimate': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.requestor': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.projectValue': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                'data.workStream': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                'data.jobStatus': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                'data.permitStatus': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                'data.jobPriority': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                'data.postcode': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.jobStartDate': {
                    value: null,
                    matchMode: FilterMatchMode.DATE_IS
                }
            }
        };

        this.state = {
            page: page,
            table: table,
            selectedRow: undefined
        };

    }

    public componentDidMount() : void {
        this.m_interval = setInterval(this.page_onLoadData, 300000);
    }

    public componentWillUnmount(): void {
        clearInterval(this.m_interval);
    }

    private isOpenButtonDisabled(): boolean {
        return this.state.selectedRow === undefined;
    }

    private requests_Click(): void {
        this.props.navigate(`/requests`);
    }

    private crews_Click(): void {
        this.props.navigate('/crews');
    }

    private calendarView_Click(): void {
        this.props.navigate('/calendarview');
    }

    private dayView_Click(): void {
        this.props.navigate('/dayview');
    }

    private invoices_Click(): void {
        this.props.navigate('/invoices');
    }

    private drawings_Click(): void {
        this.props.navigate('/drawings');
    }

    private commercials_Click(): void {
        this.props.navigate('/commercials');
    }

    private async table_onSelectionChange(selection: RequestRow | RequestRow[] | undefined): Promise<void> {

        this.safeSetState((draft: State) =>
            draft.selectedRow = selection as RequestRow | undefined);

        const navigateTo = selection as RequestRow | undefined
        if(navigateTo !== undefined) {
            const userName = this.props.msalContext.accounts[0].username;

            const lockResult = await this.m_lockClient.callWithReply(
                LockReply, (client) => client.get(navigateTo!.data.id)
            );
            lockResult.throwIfFailed();
            if(lockResult.reply!.lockData!.isLocked && lockResult.reply!.lockData!.lockingUser !== userName){
                this.m_messageDialogRef.current!.show(
                    'Booking in use',
                    `Booking ${navigateTo.data.estimate} is currently in use by '${lockResult.reply!.lockData!.lockingUser}'`,
                    MSuccessIcon);
            }
            else{
                await this.m_lockClient.call(
                    (client) => client.create(new CreateLockRequest(
                        {
                             itemId: navigateTo.data.id
                        })
                ));
                this.props.navigate(`/bookings/${navigateTo!.data.id}`);
            }
        }

    }

    private async page_onLoadData(): Promise<void> {

        //Get Row Data
        const listResult = await this.m_requestClient.callWithReply(
            RequestMultiReply, (client) => client.getBookingList());
        listResult.throwIfFailed();

        this.safeSetState((draft: State) => {

            draft.table.rowData = listResult.reply!.requests.map((w: Request) => {
                return {data: w} as RequestRow;
            });

        });
    }

    private getToolbarTemplate(): ReactElement<MToolbar> {
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const planningMember = roles?.includes('Planner') === true;

        return (
            <MToolbar>
                <MButton label="CAD Requests"
                         color="info"
                         icon={"pi pi-question"}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.requests_Click}/>
                <MSpacer/>
                {!planningMember && <React.Fragment>
                <MButton label="TM Crews"
                         color="info"
                         icon={"pi pi-users"}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.crews_Click}/>
                <MSpacer/>
                <MButton label="Calendar View"
                         color="info"
                         icon={"pi pi-calendar"}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.calendarView_Click}/>
                <MSpacer/>
                <MButton label="Day View"
                         color="info"
                         icon={"pi pi-calendar"}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.dayView_Click}/>
                <MSpacer/>
                <MButton label="Purchase Orders"
                         color="info"
                         icon={"pi pi-pound"}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.invoices_Click}/>
                <MSpacer/>
                <MButton label="Drawings"
                         color="info"
                         icon={"pi pi-images"}
                         disabled={false}
                         onClick={this.drawings_Click}/>
                <MSpacer/>
                <MButton label="Commercials"
                         color="info"
                         icon={"pi pi-wallet"}
                         disabled={false}
                         onClick={this.commercials_Click}/>
                </React.Fragment>
                }
            </MToolbar>
        );

    }

    private dateFilterTemplate = (options: any) => {
        return <Calendar value={options.value}
                         onChange={(e: any) => options.filterApplyCallback(e.value, options.index)}
                         dateFormat="dd/mm/yy"
                         placeholder="dd/mm/yyyy"
                         mask="99/99/9999"/>;
    };

    private getProjectFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList : MDropDownItem<number>[] = (
            this.context.enums!.projectList
                .map((enumvalue) => {
                    return {label: enumvalue.code + ' - ' + enumvalue.name, value: enumvalue.id};
                })
        );

        return (
            <MMultiDropDown id="projectFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private getWorkStreamFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList : MDropDownItem<number>[] = (
            this.context.enums!.workStreams
                .map((enumvalue) => {
                    return {label: enumvalue.name, value: enumvalue.id};
                })
        );

        return (
            <MMultiDropDown id="workstreamFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private getPriorityFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList : MDropDownItem<number>[] = (
            this.context.enums!.jobPriority
                               .map((enumvalue) => {
                                   return {label: enumvalue.name, value: enumvalue.id};
                               })
        );

        return (
            <MMultiDropDown id="priorityFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private getSlaFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList : MDropDownItem<number>[] = (
            this.context.enums!.slaTypes
                .map((enumvalue) => {
                    return {label: enumvalue.name, value: enumvalue.id};
                })
        );

        return (
            <MMultiDropDown id="slaTypeFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private getQuoteStatusFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList: MDropDownItem<number>[] = (
            this.context.enums!.quoteStatuses
                .map((enumValue) => {
                    return {label: enumValue.name, value: enumValue.id};
                })
        );

        return (
            <MMultiDropDown id="quoteStatusFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private getJobStatusFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList: MDropDownItem<number>[] = (
            this.context.enums!.jobStatuses
                .map((enumValue) => {
                    return {label: enumValue.name, value: enumValue.id};
                })
        );

        return (
            <MMultiDropDown id="jobStatusFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private getPermitStatusFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {
        if(this.context.enums === null) {
            return <div></div>;
        }

        const itemList: MDropDownItem<number>[] = (
            this.context.enums!.permitStatuses
                .map((enumValue) => {
                    return {label: enumValue.name, value: enumValue.id};
                })
        );

        return (
            <MMultiDropDown id="permitStatusFilter"
                            items={itemList}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );
    }

    private projectTemplate = (rowData: RequestRow) => {
        const index = this.context.enums?.projectList.findIndex(value => value.id === rowData.data.projectValue);

        if(index === undefined || index === -1) {
            return null;
        }

        return <span style={{fontSize: 'x-small'}}>
            <div>{this.context.enums?.projectList[index!].code}</div>
            <div>{this.context.enums?.projectList[index!].name}</div>
        </span>;
    };

    private workStreamTemplate = (rowData: RequestRow) => {
        const index = this.context.enums?.workStreams.findIndex(value => value.id === rowData.data.workStream)

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.workStreams[index!].name;
    };

    private priorityTemplate = (rowData: RequestRow) => {
        const index = this.context.enums?.jobPriority.findIndex(value => value.id === rowData.data.jobPriority)

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.jobPriority[index!].name;
    };

    private slaTypeTemplate = (rowData: RequestRow) => {
        const index = this.context.enums?.slaTypes.findIndex(value => value.id === rowData.data.slaType);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.slaTypes[index!].name;
    };

    private quoteStatusTemplate = (rowData: RequestRow) => {
        const index = this.context.enums?.quoteStatuses.findIndex(value => value.id === rowData.data.quoteStatus);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.quoteStatuses[index!].name;
    }

    private jobStatusTemplate = (rowData: RequestRow) => {
        const index = this.context.enums?.jobStatuses.findIndex(value => value.id === rowData.data.jobStatus);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.jobStatuses[index!].name;
    }

    private permitStatusTemplate = (rowData: RequestRow) => {
        const index = this.context.enums?.permitStatuses.findIndex(value => value.id === rowData.data.permitStatus);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.permitStatuses[index!].name;
    }

    private startDateTemplate = (rowData: RequestRow) : ReactElement => {
        try {
            //Add working days to the creation date to get the due Date.
            let dueDate = new Date(rowData.data.jobStartDate!);

            return <div>
                {dueDate.toLocaleDateString()}
            </div>
        } catch {
            return <div>{''}</div>;
        }
    };

    public render(): ReactElement {
        return (
            <MPage title="Bookings"
                   locked={this.state.page.locked}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <MessageDialog ref={this.m_messageDialogRef}/>

                <MTable
                    rowKey="data.estimate"
                    rowData={this.state.table.rowData}
                    filters={this.state.table.filters}
                    onSelectionChange={this.table_onSelectionChange}
                    sortMode={'multiple'}
                    rowsPerPage={this.state.table.rowData.length}
                    multiSortMeta={[
                        {field: 'data.jobPriority', order: 1},
                        {field: 'data.estimate', order: 1}
                    ]}>

                    <Column field="data.estimate"
                            header="Estimate"
                            filter={true}
                            sortable={true}
                            style={{width: '8%'}}/>
                    <Column field="data.locked"
                            header="Locked"
                            filter={false}
                            sortable={false}
                            body={(rowData: RequestRow) => rowData.data.locked ? 'Yes' : ''}
                            style={{width: '5%'}}/>
                    <Column field="data.requestor"
                            header="Requestor"
                            filter={true}
                            sortable={true}
                            style={{width: '8%'}}/>
                    <Column field="data.projectValue"
                            header="Project Code"
                            filter={true}
                            filterElement={this.getProjectFilterTemplate}
                            showClearButton={false}
                            showFilterMenu={false}
                            sortable={true}
                            body={this.projectTemplate}
                            style={{width: '10%'}}/>
                    <Column field="data.workStream"
                            header="Work Stream"
                            filter={true}
                            filterElement={this.getWorkStreamFilterTemplate}
                            showClearButton={false}
                            showFilterMenu={false}
                            sortable={true}
                            body={this.workStreamTemplate}
                            style={{width: '12%'}}/>
                    <Column field="data.postcode"
                            header="Postcode"
                            filter={true}
                            sortable={true}
                            style={{width: '8%'}}/>
                    <Column field="data.jobPriority"
                            header="Priority"
                            filter={true}
                            filterElement={this.getPriorityFilterTemplate}
                            showClearButton={false}
                            showFilterMenu={false}
                            sortable={true}
                            body={this.priorityTemplate}
                            style={{width: '12%'}}/>
                    <Column field="data.jobStatus"
                            header="Booking Status"
                            filter={true}
                            filterElement={this.getJobStatusFilterTemplate}
                            showClearButton={false}
                            showFilterMenu={false}
                            sortable={true}
                            body={this.jobStatusTemplate}
                            style={{width: '12%'}}/>
                    <Column field="data.permitStatus"
                            header="Permit Status"
                            filter={true}
                            filterElement={this.getPermitStatusFilterTemplate}
                            showClearButton={false}
                            showFilterMenu={false}
                            sortable={true}
                            body={this.permitStatusTemplate}
                            style={{width: '10%'}}/>
                    <Column header="Site Docs"
                            body={(rowData: RequestRow) => rowData.data.siteDocsComplete ? 'Yes' : 'No'}
                            filter={false}
                            sortable={true}
                            style={{width: '5%'}}/>
                    <Column field="data.jobStartDate"
                            header="Start Date"
                            dataType="date"
                            filter={true}
                            filterElement={this.dateFilterTemplate}
                            sortable={true}
                            body={this.startDateTemplate}
                            style={{width: '20%'}}/>
                </MTable>
            </MPage>
        );
    }
}

export default withMsal(WithRouter(ListBookingsPage));